import React, { useState } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, AppBar, Toolbar, InputBase, Avatar, IconButton, Menu, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';

const SIDEBAR_WIDTH = 260;

export default function DashboardLayout({ 
  user, 
  onLogout, 
  selected, 
  onNavigationChange, 
  navigationItems, 
  children,
  userRole = 'User',
  accessLevel = 'Standard Access'
}) {
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    if (onLogout) {
      onLogout();
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Sidebar */}
      <Drawer
        variant="persistent"
        open={sidebarOpen}
        sx={{
          width: sidebarOpen ? SIDEBAR_WIDTH : 0,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          [`& .MuiDrawer-paper`]: { 
            width: SIDEBAR_WIDTH, 
            boxSizing: 'border-box', 
            bgcolor: '#f8fafc', 
            borderRight: '1px solid #e0e0e0',
            position: 'relative',
            overflow: 'hidden'
          },
        }}
      >
        <Toolbar sx={{ minHeight: 64 }} />
        <Box sx={{ p: 2 }}>
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton 
                  selected={selected === item.key} 
                  onClick={() => onNavigationChange(item.key)} 
                  sx={{ borderRadius: 2, mb: 1 }}
                >
                  <ListItemIcon>
                    {React.cloneElement(item.icon, { 
                      color: selected === item.key ? 'primary' : 'action' 
                    })}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography fontWeight={selected === item.key ? 600 : 400}>
                        {item.label}
                      </Typography>
                    } 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ px: 2 }}>
          <Typography variant="caption" color="text.secondary">
            User Role: {userRole}
          </Typography>
          <List>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2 }}>
                <ListItemText 
                  primary={
                    <Typography fontSize={14}>
                      Access Level: {accessLevel}
                    </Typography>
                  } 
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1, 
        bgcolor: '#f5f5f5'
      }}>
        {/* Top Bar */}
        <AppBar position="static" elevation={0} sx={{ bgcolor: '#fff', color: 'text.primary', borderBottom: '1px solid #e0e0e0' }}>
          <Toolbar>
            <IconButton edge="start" sx={{ mr: 2 }} onClick={toggleSidebar}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ position: 'relative', flex: 1, maxWidth: 400 }}>
              <Box sx={{ position: 'absolute', height: '100%', display: 'flex', alignItems: 'center', pl: 2 }}>
                <SearchIcon color="action" />
              </Box>
              <InputBase
                placeholder="Search…"
                sx={{
                  bgcolor: '#f1f3f4',
                  borderRadius: 2,
                  pl: 5,
                  pr: 2,
                  py: 0.5,
                  width: '100%',
                  fontSize: 16,
                }}
                inputProps={{ 'aria-label': 'search' }}
              />
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Welcome, {user?.name || userRole}
              </Typography>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ ml: 1 }}
              >
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                  {user?.name?.charAt(0).toUpperCase() || userRole.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        {/* Main Area */}
        <Box sx={{ p: 2 }}>{children}</Box>
        
        {/* Profile Menu */}
        <Menu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleProfileMenuClose} sx={{ minWidth: 200 }}>
            <AccountCircleIcon sx={{ mr: 2 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {user?.name || userRole}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userRole}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <LogoutIcon sx={{ mr: 2 }} />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
} 